/**
 * Route preview sheet: destination, alternatives with traffic-aware times AND
 * the things the ETA hides (how many traffic lights, how many turns, tolls),
 * avoid-toggles that re-route immediately, vehicle indicator, GO button.
 *
 * The traffic-light count is here because the driver pointed out that it
 * changes which route is actually better: a road with eighteen fewer signals
 * is a calmer, more predictable drive even when it shows a minute or two
 * longer, and the routing engine will never surface that trade on its own.
 *
 * Above the list sits the AI advice card when the model has an opinion worth
 * showing - see ai/advisor.ts.
 */

import type { Place, Route } from '../core/types';
import type { RouteStats } from '../nav/routeStats';
import type { RouteAdvice } from '../ai/advisor';
import { formatDistance, formatDuration, formatEta } from '../core/format';
import { summariseRoute } from '../nav/directions';
import { store } from '../core/store';
import { activeVehicle } from '../core/settings';
import { t } from '../core/i18n';

export interface PreviewState {
  destination: Place;
  routes: Route[];
  stats: RouteStats[];
  selectedIndex: number;
  loading: boolean;
  error: string | null;
  isSaved?: boolean;
  advice?: RouteAdvice | null;
  adviceLoading?: boolean;
}

export class RoutePreview {
  readonly el: HTMLElement;

  constructor(
    parent: HTMLElement,
    private actions: {
      onGo: () => void;
      onCancel: () => void;
      onSelectRoute: (index: number) => void;
      onToggleAvoid: (key: 'avoidTolls' | 'avoidMotorways', value: boolean) => void;
      onSwitchVehicle: () => void;
      onSavePlace: () => void;
      onDismissAdvice: () => void;
    },
  ) {
    this.el = document.createElement('div');
    this.el.className = 'sheet sheet--preview';
    this.el.hidden = true;
    parent.appendChild(this.el);
  }

  hide(): void {
    this.el.hidden = true;
  }

  render(state: PreviewState): void {
    const s = store.get().settings;
    const vehicle = activeVehicle(s);
    const { destination, routes, stats, selectedIndex, loading, error } = state;
    this.el.hidden = false;

    // Which option has the fewest signals, so it can be badged even when it
    // isn't the fastest.
    const calmest =
      routes.length > 1 && stats.length === routes.length
        ? stats.reduce(
            (best, cur, i) => (cur.trafficSignals < stats[best].trafficSignals ? i : best),
            0,
          )
        : -1;

    const routesHtml = loading
      ? `<div class="sheet__loading"><span class="spinner"></span> ${t('preview.finding')}</div>`
      : error
        ? `<div class="sheet__error">${escapeHtml(error)}</div>`
        : routes
            .map((r, i) => {
              const selected = i === selectedIndex;
              const hasTraffic = r.duration_typical != null && r.duration_typical > 0;
              const delay = hasTraffic ? r.duration - (r.duration_typical ?? r.duration) : 0;
              const st = stats[i];
              const showCalmest = i === calmest && st && st.trafficSignals > 0 && calmest !== 0;
              return `
              <button class="route-opt ${selected ? 'is-selected' : ''}" data-i="${i}">
                <span class="route-opt__time">${formatDuration(r.duration)}
                  ${r._tag ? `<em class="route-opt__tag route-opt__tag--${r._tag}">${t(`route.${r._tag}`)}</em>` : ''}
                  ${showCalmest ? `<em class="route-opt__tag route-opt__tag--calm">${t('stats.calmest')}</em>` : ''}
                </span>
                <span class="route-opt__meta">
                  <span>${formatDistance(r.distance, s.units)} · ${escapeHtml(summariseRoute(r))}</span>
                  ${
                    delay > 180
                      ? `<span class="route-opt__delay">${t('preview.traffic', { d: formatDuration(delay) })}</span>`
                      : hasTraffic
                        ? `<span class="route-opt__clear">${t('preview.clear')}</span>`
                        : ''
                  }
                </span>
                ${st ? routeStatsRow(st) : ''}
                <span class="route-opt__eta">${t('preview.eta', { t: formatEta(r.duration) })}</span>
              </button>`;
            })
            .join('');

    this.el.innerHTML = `
      <div class="sheet__grab"></div>
      <div class="sheet__dest">
        <div class="sheet__dest-text">
          <div class="sheet__dest-name">${escapeHtml(destination.name)}</div>
          <div class="sheet__dest-addr">${escapeHtml(destination.address)}</div>
        </div>
        <button class="icon-btn sheet__save ${state.isSaved ? 'is-saved' : ''}" data-act="save"
                aria-label="${state.isSaved ? 'Saved for the fleet' : 'Save for the fleet'}" ${state.isSaved ? 'disabled' : ''}>
          ${STAR_SVG}
        </button>
      </div>
      ${this.adviceHtml(state)}
      <div class="sheet__routes">${routesHtml}</div>
      <div class="sheet__chips">
        <button class="chip ${s.route.avoidTolls ? 'is-on' : ''}" data-avoid="avoidTolls">${t('preview.avoidTolls')}</button>
        <button class="chip ${s.route.avoidMotorways ? 'is-on' : ''}" data-avoid="avoidMotorways">${t('preview.avoidMotorways')}</button>
        <button class="chip chip--vehicle" data-act="vehicle">
          ${vehicle.kind === 'truck' ? TRUCK_SVG : CAR_SVG} ${escapeHtml(vehicle.name)}
        </button>
      </div>
      <div class="sheet__actions">
        <button class="btn btn--ghost" data-act="cancel">${t('preview.cancel')}</button>
        <button class="btn btn--primary btn--go" data-act="go" ${loading || !!error || !routes.length ? 'disabled' : ''}>
          ${GO_SVG} ${t('preview.start')}
        </button>
      </div>`;

    this.el.querySelectorAll<HTMLButtonElement>('.route-opt').forEach((b) =>
      b.addEventListener('click', () => this.actions.onSelectRoute(Number(b.dataset.i))),
    );
    this.el.querySelectorAll<HTMLButtonElement>('[data-avoid]').forEach((b) =>
      b.addEventListener('click', () => {
        const key = b.dataset.avoid as 'avoidTolls' | 'avoidMotorways';
        this.actions.onToggleAvoid(key, !store.get().settings.route[key]);
      }),
    );
    this.el.querySelector('[data-act=vehicle]')!.addEventListener('click', () => this.actions.onSwitchVehicle());
    this.el.querySelector('[data-act=save]')?.addEventListener('click', () => this.actions.onSavePlace());
    this.el.querySelector('[data-act=cancel]')!.addEventListener('click', () => this.actions.onCancel());
    this.el.querySelector('[data-act=go]')?.addEventListener('click', () => this.actions.onGo());
    this.el
      .querySelector('[data-act=use-advice]')
      ?.addEventListener('click', () =>
        this.actions.onSelectRoute(Number(state.advice?.pick ?? selectedIndex)),
      );
    this.el
      .querySelector('[data-act=dismiss-advice]')
      ?.addEventListener('click', () => this.actions.onDismissAdvice());
  }

  private adviceHtml(state: PreviewState): string {
    if (state.adviceLoading) {
      return `<div class="advice advice--loading"><span class="spinner"></span> ${t('ai.thinking')}</div>`;
    }
    const a = state.advice;
    if (!a) return '';
    const already = a.pick === state.selectedIndex;
    return `
      <div class="advice advice--${a.confidence}">
        <div class="advice__head">
          <span class="advice__badge">${t('ai.badge')}</span>
          <button class="advice__close" data-act="dismiss-advice" aria-label="${t('ai.dismiss')}">
            <svg viewBox="0 0 24 24"><path d="M6 6 L18 18 M18 6 L6 18" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>
          </button>
        </div>
        <div class="advice__headline">${escapeHtml(a.headline)}</div>
        <div class="advice__reason">${escapeHtml(a.reason)}</div>
        ${
          a.tips.length
            ? `<ul class="advice__tips">${a.tips.map((x) => `<li>${escapeHtml(x)}</li>`).join('')}</ul>`
            : ''
        }
        ${
          already
            ? ''
            : `<button class="btn btn--small btn--primary advice__use" data-act="use-advice">${t('ai.use')}</button>`
        }
      </div>`;
  }
}

/** The line the ETA doesn't tell you: stops, turns, tolls. */
function routeStatsRow(st: RouteStats): string {
  const bits: string[] = [];
  if (st.trafficSignals > 0) {
    bits.push(
      `<span class="route-stat"><svg viewBox="0 0 24 24" aria-hidden="true"><rect x="8" y="3" width="8" height="15" rx="2.4" fill="none" stroke="currentColor" stroke-width="1.8"/><circle cx="12" cy="7.5" r="1.5" fill="currentColor"/><circle cx="12" cy="14" r="1.5" fill="currentColor" opacity="0.45"/><path d="M12 18 V21" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>${t(
        'stats.signals',
        { n: st.trafficSignals },
      )}</span>`,
    );
  }
  if (st.turns > 0) bits.push(`<span class="route-stat">${t('stats.turns', { n: st.turns })}</span>`);
  if (st.tollBooths > 0) bits.push(`<span class="route-stat">${t('stats.tolls', { n: st.tollBooths })}</span>`);
  if (!bits.length) return '';
  return `<span class="route-opt__stats">${bits.join('')}</span>`;
}

const GO_SVG = `<svg viewBox="0 0 24 24" class="btn__icon"><path d="M3 11 L21 3 L13 21 L11 13 Z" fill="currentColor"/></svg>`;
const STAR_SVG = `<svg viewBox="0 0 24 24"><path d="M12 3 L14.7 8.6 L20.9 9.4 L16.4 13.7 L17.5 19.8 L12 16.9 L6.5 19.8 L7.6 13.7 L3.1 9.4 L9.3 8.6 Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/></svg>`;
const CAR_SVG = `<svg viewBox="0 0 24 24" class="chip__icon"><path d="M5 11 L6.5 6.5 C6.8 5.6 7.6 5 8.5 5 h7 c.9 0 1.7.6 2 1.5 L19 11 M4 11 h16 v5 a1 1 0 0 1 -1 1 h-1 a1 1 0 0 1 -1 -1 v-1 H7 v1 a1 1 0 0 1 -1 1 H5 a1 1 0 0 1 -1 -1 Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const TRUCK_SVG = `<svg viewBox="0 0 24 24" class="chip__icon"><path d="M2 6 h11 v9 H2 Z M13 9 h4 l3 3 v3 h-7 Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/><circle cx="6" cy="17.5" r="1.8" fill="currentColor"/><circle cx="17" cy="17.5" r="1.8" fill="currentColor"/></svg>`;

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}
